import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { EmailRequestType, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { APP_PAGES } from '../../../routes/constants';
import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useInboxEntriesStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';
import { getCurrentTabParam } from '../../../utils/getCurrentTabParam';
import { useCustomToast } from '../../useCustomToast';

export const useArchiveUserCommunicationForDoBox = (inboxEntryId?: string) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { errorMessage } = useCustomToast();

  const handleArchiveUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      options?: { onSuccess?: () => void; shouldNavigate?: boolean },
    ) => {
      const {
        getCommunicationsByInboxEntryId,
        setArchiveLoading,
        updateUserCommunicationLabelsInInboxEntries,
      } = useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      const { updateUserCommunicationLabels } =
        useUserCommunicationsStore.getState();
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      if (!inboxEntryId) {
        return;
      }
      const userCommunications = getCommunicationsByInboxEntryId(inboxEntryId);

      const currentUserCommunications = [...(userCommunications ?? [])];
      const communicationsToArchive = messageIdsArray
        .map((messageId) => {
          const userCommunication = currentUserCommunications.find(
            (comm) => comm.messageId === messageId,
          );
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (communicationsToArchive.length === 0) {
        errorMessage(t('inbox.userCommunication.actions.archiveError'));
        return;
      }

      setArchiveLoading(true);

      try {
        const operations = communicationsToArchive.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.ARCHIVE,
              });

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to archive email for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);
        const succeeded = results.filter((result) => result.success);

        if (succeeded.length) {
          updateUserCommunicationLabelsInInboxEntries(
            succeeded.map(({ messageId }) => messageId),
            (currentLabels) => getLabelIdsForAction(currentLabels, 'archive'),
          );

          succeeded.forEach(({ messageId, communication }) => {
            const newLabelIds = getLabelIdsForAction(
              communication.labelIds,
              'archive',
            );
            updateUserCommunicationLabels(messageId, newLabelIds);
          });
        }

        if (failed.length === 0) {
          log.info(
            `${communicationsToArchive.length} messages archived successfully`,
          );

          options?.onSuccess?.();

          if (options?.shouldNavigate) {
            const currentTab = getCurrentTabParam();
            const targetUrl = currentTab
              ? `${APP_PAGES.DOBOX}?tab=${currentTab}`
              : APP_PAGES.DOBOX;

            navigate(targetUrl);
          }
        } else {
          log.warn(
            `${failed.length} operations failed out of ${communicationsToArchive.length}`,
          );

          errorMessage(
            failed.length === communicationsToArchive.length
              ? t('inbox.userCommunication.actions.archiveError')
              : t('inbox.userCommunication.actions.partialArchiveError', {
                  failed: failed.length,
                  total: communicationsToArchive.length,
                }),
          );
        }
      } catch (error) {
        log.error(error);
        errorMessage(t('inbox.userCommunication.actions.archiveError'));
      } finally {
        setArchiveLoading(false);
      }
    },
    [errorMessage, navigate, t, inboxEntryId],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleArchiveUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
